import { ActionReducerMapBuilder, createSlice, PayloadAction } from '@reduxjs/toolkit'
import { withLoadingReducer } from 'utils/reducerHandler'
import {
  GetSupplyOrderDetailItemResponseType,
  GetSupplyOrderDetailResponseType,
  GetSupplyOrderRemarksResponseType,
} from 'features/SupplyOrder/services/outboundSO'
import { GetAllLocationsResponseType } from 'features/Location/services/location'
import { GetPurchaseOrderReferencesResponseType } from 'features/PurchaseOrder/services/references'
import {
  SLICE_NAME,
  fetchGetSupplyOrderDetail,
  fetchGetSupplyOrderType,
  fetchGetSupplyOrderDetailItem,
  fetchGetAllLocation,
  fetchGetSupplyOrderRemarks,
  fetchGetPurchaseOrderReferences,
  fetchPutSupplyOrderDetail,
} from './thunk'

export type OutboundSOAddEditSliceType = {
  isLoading: boolean
  data: Nullable<GetSupplyOrderDetailResponseType['data']>
  products: {
    query: Partial<{
      pageIndex: number
      pageSize: number
      name: string
    }>
    item: GetSupplyOrderDetailItemResponseType['data']['content']
  }
  constants: {
    remarks: GetSupplyOrderRemarksResponseType['data']
    locations: GetAllLocationsResponseType
    type: string[]
    poRefferences: GetPurchaseOrderReferencesResponseType['data']['content']
  }
  form: {
    poReff?: Nullable<GetPurchaseOrderReferencesResponseType['data']['content'][number]>
    soReff: string
    soType: string
    soDate: Date | null
    shippingInstructions: string
    email: string
    remarks: Nullable<GetSupplyOrderRemarksResponseType['data'][number]>
    origin: Nullable<GetAllLocationsResponseType[number]>
    destination: Nullable<GetAllLocationsResponseType[number]>
  }
  errorField: {
    email?: string
  }
}

const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/

const initialState: OutboundSOAddEditSliceType = {
  isLoading: false,
  data: null,
  products: {
    query: {
      pageIndex: 0,
      pageSize: 10,
    },
    item: [],
  },
  constants: {
    remarks: [],
    locations: [],
    type: [],
    poRefferences: [],
  },
  form: {
    soReff: '',
    soType: '',
    soDate: new Date(),
    shippingInstructions: '',
    email: '',
    remarks: null,
    origin: null,
    destination: null,
  },
  errorField: {
    email: undefined,
  },
}

const outboundSOAddEditSlice = createSlice({
  name: SLICE_NAME,
  initialState,
  reducers: {
    reset: () => initialState,
    setForm: (
      state,
      {
        payload,
      }: PayloadAction<{
        key: keyof OutboundSOAddEditSliceType['form']
        value: OutboundSOAddEditSliceType['form'][keyof OutboundSOAddEditSliceType['form']]
      }>,
    ) => {
      const { key, value } = payload
      state.form[key] = value as never

      if (key === 'email') {
        if (!value) {
          state.errorField.email = 'Email wajib diisi'
        } else if (typeof value === 'string' && !emailRegex.test(value)) {
          state.errorField.email = 'Format email tidak valid'
        } else {
          state.errorField.email = undefined
        }
      }

      if (key === 'poReff') {
        state.form.origin =
          state.constants.locations.find(
            (loc) => loc.location_id === (value as { destinationId: number })?.destinationId,
          ) || null
        state.form.destination = null
      }
    },
    setAllFormValues: (
      state,
      { payload }: PayloadAction<Partial<OutboundSOAddEditSliceType['form']>>,
    ) => {
      state.form = payload as OutboundSOAddEditSliceType['form']
    },
  },
  extraReducers: withLoadingReducer(
    (builder: ActionReducerMapBuilder<OutboundSOAddEditSliceType>) => {
      builder.addCase(fetchGetSupplyOrderDetail.fulfilled, (state, { payload }) => {
        state.data = payload.data
      })
      builder.addCase(fetchGetSupplyOrderDetailItem.pending, (state, action) => {
        state.products.query = {
          ...state.products.query,
          ...action.meta.arg.params,
        }
      })
      builder.addCase(fetchGetSupplyOrderDetailItem.fulfilled, (state, { payload }) => {
        state.products.item = payload
      })
      builder.addCase(fetchGetSupplyOrderType.fulfilled, (state, { payload }) => {
        state.constants.type = payload
      })
      builder.addCase(fetchGetAllLocation.fulfilled, (state, { payload }) => {
        state.constants.locations = payload
      })
      builder.addCase(fetchGetSupplyOrderRemarks.fulfilled, (state, { payload }) => {
        state.constants.remarks = payload
      })
      builder.addCase(fetchGetPurchaseOrderReferences.fulfilled, (state, { payload }) => {
        state.constants.poRefferences = payload
      })
    },
    [
      fetchGetSupplyOrderDetail,
      fetchGetSupplyOrderDetailItem,
      fetchGetPurchaseOrderReferences,
      fetchPutSupplyOrderDetail,
    ],
  ),
})

export const { reset, setForm, setAllFormValues } = outboundSOAddEditSlice.actions
export default outboundSOAddEditSlice.reducer
